import { create } from 'zustand';
import { notificationsApi, Notification } from '@/lib/api';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isOpen: boolean;
  loading: boolean;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  addNotification: (notification: Notification) => void;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isOpen: false,
  loading: false,

  fetchNotifications: async () => {
    set({ loading: true });
    try {
      const { items } = await notificationsApi.getAll({ limit: 30 });
      set({ notifications: items, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await notificationsApi.getUnreadCount();
      set({ unreadCount: count });
    } catch { /* */ }
  },

  markAsRead: async (id: number) => {
    try {
      await notificationsApi.markAsRead(id);
      set(state => ({
        notifications: state.notifications.map(n =>
          n.id === id ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch { /* */ }
  },

  markAllAsRead: async () => {
    try {
      await notificationsApi.markAllAsRead();
      set(state => ({
        notifications: state.notifications.map(n => ({ ...n, is_read: true })),
        unreadCount: 0,
      }));
    } catch { /* */ }
  },

  deleteNotification: async (id: number) => {
    try {
      await notificationsApi.delete(id);
      set(state => {
        const n = state.notifications.find(n => n.id === id);
        return {
          notifications: state.notifications.filter(n => n.id !== id),
          unreadCount: n && !n.is_read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
        };
      });
    } catch { /* */ }
  },

  addNotification: (notification: Notification) => {
    set(state => ({
      notifications: [notification, ...state.notifications].slice(0, 30),
      unreadCount: state.unreadCount + 1,
    }));
  },

  setOpen: (open: boolean) => set({ isOpen: open }),
  toggle: () => set(state => ({ isOpen: !state.isOpen })),
}));
